/**
 * Admin API client — calls /api/admin/* with same-origin cookies.
 * Mirrors the shape returned by apps/portal/server/src/routes/admin.ts.
 *
 * The /whoami endpoint is open to any authenticated user (returns
 * isAdmin: boolean); the other endpoints 403 with `not_admin` when the
 * signed-in account isn't in ADMIN_EMAILS.
 */

export interface WhoAmIResponse {
  email: string
  isAdmin: boolean
}

export interface OverviewResponse {
  users: {
    total: number
    last7d: number
    last30d: number
    activeLast24h: number
    signupTrend: { date: string; count: number }[]
  }
  youtube: {
    totalDownloads: number
    last7d: number
    completedCount: number
    failedCount: number
    totalBytes: number
    activeNow: number
    topUsers: {
      userSub: string
      email: string
      count: number
      totalBytes: number
    }[]
  }
  artifact: {
    total: number
    totalBytes: number
    totalViews: number
  }
  pdf: {
    totalJobs: number
    last7d: number
    activeNow: number
    completedCount: number
    originalBytes: number
    compressedBytes: number
    savedBytes: number
  }
}

export interface AdminUser {
  googleSub: string
  email: string
  name: string
  picture: string
  createdAt: string
  lastSeenAt: string
  youtubeTermsAccepted: boolean
  /** True if this user has admin access (root env admin OR DB-granted). */
  isAdmin: boolean
  /** True if admin comes from ADMIN_EMAILS env — immutable, can't be toggled. */
  isRootAdmin: boolean
  portalAccess: {
    everyone: boolean
    labels: string[]
    /** "for Brand Intelligence Consulting" 탭 + Creators 워크스페이스. 레이블이 아니라
     *  최상위 카테고리 플래그이고, everyone 과 달리 **기본이 꺼짐**(부여형)이다. */
    bic: boolean
    /** 전사 직원 — Salesforce Products·확인서 발송. 기본이 꺼짐(부여형)이다. */
    employee: boolean
  }
  youtube: {
    count: number
    completedCount: number
    totalBytes: number
    lastAt: string | null
  }
  artifact: {
    count: number
    totalBytes: number
    totalViews: number
    lastAt: string | null
  }
}

export interface UsersResponse {
  users: AdminUser[]
}

export interface YoutubePerUser {
  userSub: string
  email: string
  count: number
  completedCount: number
  totalBytes: number
  lastAt: string | null
}

export interface YoutubeTrendPoint {
  date: string
  count: number
  bytes: number
}

export interface YoutubeActiveJob {
  jobId: string
  email: string
  youtubeUrl: string
  videoTitle: string
  status: string
  progress: number
  quality: string
  createdAt: string | null
}

export interface YoutubeResponse {
  perUser: YoutubePerUser[]
  trend: YoutubeTrendPoint[]
  active: YoutubeActiveJob[]
  qualityDist: { quality: string; count: number }[]
  statusDist: { status: string; count: number }[]
  errorDist: { error: string; count: number }[]
  totals: {
    totalCount: number
    completedCount: number
    completedBytes: number
    avgBytes: number
    successRate: number
  }
}

export interface ArtifactPerUser {
  ownerSub: string
  email: string
  count: number
  totalBytes: number
  lastAt: string | null
}

export interface ArtifactRecent {
  publicId: string
  title: string
  ownerEmail: string
  type: string
  permission: string
  sizeBytes: number
  createdAt: string | null
}

export interface ArtifactMostViewed {
  publicId: string
  title: string
  ownerEmail: string
  permission: string
  viewCount: number
}

export interface ArtifactResponse {
  perUser: ArtifactPerUser[]
  permissionDist: { permission: string; count: number }[]
  typeDist: { type: string; count: number }[]
  recent: ArtifactRecent[]
  mostViewed: ArtifactMostViewed[]
  totals: {
    total: number
    totalBytes: number
    totalViews: number
    avgBytes: number
  }
}

async function json<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${path} failed: ${res.status} ${text.slice(0, 200)}`)
  }
  return (await res.json()) as T
}

export function fetchWhoAmI(): Promise<WhoAmIResponse> {
  return json<WhoAmIResponse>('/api/admin/whoami')
}

export function fetchOverview(): Promise<OverviewResponse> {
  return json<OverviewResponse>('/api/admin/overview')
}

export function fetchYoutube(): Promise<YoutubeResponse> {
  return json<YoutubeResponse>('/api/admin/youtube')
}

export function fetchArtifact(): Promise<ArtifactResponse> {
  return json<ArtifactResponse>('/api/admin/artifact')
}

export function fetchUsers(): Promise<UsersResponse> {
  return json<UsersResponse>('/api/admin/users')
}

export interface PortalAccessInput {
  everyone: boolean
  labels: string[]
  bic: boolean
  employee: boolean
}

/** Set a user's portal landing access (which top tabs / labels they see). */
export async function setUserAccess(
  googleSub: string,
  access: PortalAccessInput,
): Promise<{ ok: true; portalAccess: PortalAccessInput }> {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(googleSub)}/access`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(access),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`set access failed: ${res.status} ${text.slice(0, 200)}`)
  }
  return (await res.json()) as {
    ok: true
    portalAccess: PortalAccessInput
  }
}

/** Grant or revoke admin access for a user (DB flag). Root env admins are
 *  rejected server-side with `root_admin_immutable`. */
export async function setUserAdmin(
  googleSub: string,
  isAdmin: boolean,
): Promise<{ ok: true; isAdmin: boolean }> {
  const res = await fetch(`/api/admin/users/${encodeURIComponent(googleSub)}/admin`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ isAdmin }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`set admin failed: ${res.status} ${text.slice(0, 200)}`)
  }
  return (await res.json()) as { ok: true; isAdmin: boolean }
}

// ===== OAuth Monitor =====

export type CreatorOAuthStatus =
  | 'active'
  | 'expired'
  | 'never_connected'
  | 'revoked'

export interface CreatorMonitorRow {
  handle: string
  channelId: string
  channelName: string
  displayNameKr: string
  displayNameEn: string
  profileImageUrl: string
  isOfficialCreator: boolean
  status: CreatorOAuthStatus
  connectedAt: string | null
  lastCollectedAt: string | null
  lastCollectionStatus: 'success' | 'failed' | null
  collectedRowCount: number
  nextScheduledAt: string | null
  tokenLastVerifiedAt: string | null
  accessTokenExpiresAt: string | null
  hasAccessToken: boolean
  hasRefreshToken: boolean
  lastError: string | null
  lastErrorAt: string | null
}

export interface PendingInviteRow {
  handle: string
  channelName: string
  issuedAt: string
  expiresAt: string
  inviteUrl: string
  daysToExpiry: number
}

export interface OAuthSystemStatus {
  cronHealthy: boolean
  lastCronRunAt: string | null
  nextCronRunAt: string | null
  totalCreators: number
  activeCount: number
  expiredCount: number
  neverCount: number
  errorCount: number
}

export interface OAuthStatusResponse {
  creators: CreatorMonitorRow[]
  pendingInvites: PendingInviteRow[]
  systemStatus: OAuthSystemStatus
  pagination: { limit: number; offset: number; total: number }
}

export interface CreatorDailyRow {
  date: string
  views: number
  subscribersGained: number
  subscribersLost: number
  watchTimeMin: number
  likes: number
  comments: number
}

export interface CreatorMonitorDetail extends CreatorMonitorRow {
  recentCollections: CreatorDailyRow[]
}

export interface CollectResponse {
  success: boolean
  collectedRows: number
  durationMs: number
  errors: string[]
}

export interface CollectInProgress {
  error: 'already_in_progress'
  message: string
}

export function fetchOAuthStatus(opts?: {
  status?: CreatorOAuthStatus | 'all'
  q?: string
  limit?: number
  offset?: number
  showAll?: boolean
}): Promise<OAuthStatusResponse> {
  const params = new URLSearchParams()
  if (opts?.status) params.set('status', opts.status)
  if (opts?.q) params.set('q', opts.q)
  if (typeof opts?.limit === 'number') params.set('limit', String(opts.limit))
  if (typeof opts?.offset === 'number') params.set('offset', String(opts.offset))
  if (opts?.showAll) params.set('showAll', '1')
  const qs = params.toString()
  return json<OAuthStatusResponse>(
    `/api/admin/oauth-status${qs ? `?${qs}` : ''}`,
  )
}

export function fetchOAuthStatusDetail(handle: string): Promise<CreatorMonitorDetail> {
  return json<CreatorMonitorDetail>(
    `/api/admin/oauth-status/${encodeURIComponent(handle)}`,
  )
}

export interface TokenDiagResult {
  handle: string
  channelId: string
  token: {
    found: boolean
    status: string | null
    hasRefreshToken: boolean
    accessTokenExpiresAt: string | null
    grantedAt: string | null
    lastRefreshAt: string | null
    lastDataFetchAt: string | null
    lastError: string | null
    lastErrorAt: string | null
    storedScope: string | null
  }
  refresh: { ok: boolean; httpStatus: number | null; expiresInSec: number | null; body: string | null }
  tokeninfo: { ok: boolean; scopes: string[] | null; error: string | null }
  analytics: { ok: boolean; httpStatus: number | null; rows: number | null; error: string | null }
  channel: { ok: boolean; httpStatus: number | null; channelId: string | null; error: string | null }
  verdict: string
}

/** READ-ONLY live token probe (admin-gated). Hits the real Google APIs. */
export function fetchTokenDiag(handle: string): Promise<TokenDiagResult> {
  return json<TokenDiagResult>(
    `/api/admin/oauth-status/${encodeURIComponent(handle)}/diag/token`,
  )
}

export async function postOAuthCollect(
  handle: string,
): Promise<CollectResponse | CollectInProgress> {
  const res = await fetch(`/api/admin/oauth-collect/${encodeURIComponent(handle)}`, {
    method: 'POST',
    credentials: 'same-origin',
  })
  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (res.status === 409 && payload.error === 'already_in_progress') {
    return payload as unknown as CollectInProgress
  }
  if (!res.ok) {
    throw new Error(`oauth-collect failed: ${res.status} ${JSON.stringify(payload).slice(0, 200)}`)
  }
  return payload as unknown as CollectResponse
}

// ===== AI access tokens =====

export type AiTokenStatus = 'active' | 'expired' | 'revoked'

export interface AiTokenRow {
  id: string
  name: string
  purpose: string
  permissions: { readOnly: boolean }
  issuedBy: string
  issuedAt: string
  expiresAt: string
  revokedAt: string | null
  lastUsedAt: string | null
  usageCount: number
  status: AiTokenStatus
  daysToExpiry: number
}

export interface AiTokenListResponse {
  tokens: AiTokenRow[]
  defaults: { expiryDays: number }
}

export interface AiTokenIssueResponse {
  id: string
  name: string
  purpose: string
  issuedAt: string
  expiresAt: string
  /** Cleartext token — shown exactly once. Never returned again. */
  plaintext: string
  warning: string
}

export interface AiTokenLogRow {
  timestamp: string
  path: string
  method: string
  statusCode: number
  ip: string
  userAgent: string
}

export function fetchAiTokens(): Promise<AiTokenListResponse> {
  return json<AiTokenListResponse>('/api/admin/ai-tokens')
}

export async function issueAiToken(input: {
  name: string
  purpose?: string
  expiryDays?: number
}): Promise<AiTokenIssueResponse> {
  const res = await fetch('/api/admin/ai-tokens', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) {
    throw new Error(`issue failed: ${res.status} ${JSON.stringify(payload).slice(0, 200)}`)
  }
  return payload as unknown as AiTokenIssueResponse
}

export async function revokeAiToken(id: string): Promise<{ ok: true }> {
  const res = await fetch(`/api/admin/ai-tokens/${encodeURIComponent(id)}/revoke`, {
    method: 'POST',
    credentials: 'same-origin',
  })
  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) {
    throw new Error(`revoke failed: ${res.status} ${JSON.stringify(payload).slice(0, 200)}`)
  }
  return payload as { ok: true }
}

export function fetchAiTokenLogs(id: string): Promise<{ logs: AiTokenLogRow[] }> {
  return json<{ logs: AiTokenLogRow[] }>(
    `/api/admin/ai-tokens/${encodeURIComponent(id)}/logs`,
  )
}

/**
 * Future-tense relative formatter for "next scheduled cron run".
 * Different shape than formatRelKo (which is past-only):
 *   · already past:           "곧"
 *   · within today (KST):     "오늘 03:00"
 *   · tomorrow (KST):         "내일 03:00"
 *   · more than 24h out:      absolute YYYY-MM-DD HH:mm
 */
export function formatNextRun(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const diffMs = d.getTime() - Date.now()
  if (diffMs <= 0) return '곧'
  if (diffMs > 24 * 60 * 60 * 1000) {
    return formatDate(iso)
  }
  // Same vs next calendar day (browser local TZ — operator's machine).
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  const hh = d
    .toLocaleString('en-CA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    // Strip any locale weirdness — keep "HH:mm" only.
    .replace(/[^0-9:]/g, '')
  return sameDay ? `오늘 ${hh}` : `내일 ${hh}`
}

/** "5분 전" / "3시간 전" / "2일 전" — short Korean relative time. */
export function formatRelKo(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000)
  if (sec < 60) return '방금'
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}분 전`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}시간 전`
  const day = Math.floor(h / 24)
  if (day < 30) return `${day}일 전`
  const mo = Math.floor(day / 30)
  if (mo < 12) return `${mo}개월 전`
  return `${Math.floor(day / 365)}년 전`
}

/** Pretty-print a byte count, GB-first since downloads are big. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  const decimals = i >= 3 ? 2 : i >= 2 ? 1 : 0
  return `${n.toFixed(decimals)} ${units[i]}`
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  // KST-ish display — the audience is the CEO in Korea, so render in
  // local time. toLocaleString does the right thing if the browser
  // locale matches; we explicitly request en-CA for an ISO-ish format
  // that's still readable.
  return d.toLocaleString('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

// ===== Creator Dashboards (Phase 4) — registration / list / meta / delete =====
export type ChannelPlatform = 'youtube' | 'instagram' | 'tiktok'

export interface AdminChannel {
  platform: ChannelPlatform
  url: string
  handle: string
  title: string
  externalId: string
  profileImageUrl: string
  subscriberCount: number
  dataSource: 'manual' | 'api'
  connected: boolean
  /** False once the platform is API-connected — the collector owns the number. */
  editable: boolean
  lastCollectedAt: string | null
}

export interface AdminCreator {
  handle: string
  channelId: string
  channelTitle: string
  displayNameKr: string
  displayNameEn: string
  profileImageUrl: string
  subscriberCount: number
  label: string
  dashboardEnabled: boolean
  lastCollectedAt: string | null
  /** Multi-platform channels (YouTube/Instagram/TikTok). */
  channels: AdminChannel[]
}

export function fetchCreators(label?: string): Promise<{ creators: AdminCreator[] }> {
  const qs = label ? `?label=${encodeURIComponent(label)}` : ''
  return json<{ creators: AdminCreator[] }>(`/api/admin/creators${qs}`)
}

async function mutate<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    let msg = `${res.status}`
    try {
      const d = (await res.json()) as { message?: string; error?: string }
      msg = d.message || d.error || msg
    } catch {
      /* ignore */
    }
    throw new Error(msg)
  }
  return (res.status === 204 ? undefined : await res.json()) as T
}

export function postCreator(
  label: string,
  youtubeUrl: string,
): Promise<{ created: boolean; creator: AdminCreator }> {
  return mutate('/api/admin/creators', 'POST', { label, youtubeUrl })
}

export function deleteCreator(handle: string): Promise<{ ok: true }> {
  return mutate(`/api/admin/creators/${encodeURIComponent(handle)}`, 'DELETE')
}

export function patchCreatorMeta(
  handle: string,
  patch: { dashboardEnabled?: boolean; label?: string },
): Promise<{ handle: string; dashboardEnabled: boolean; label: string }> {
  return mutate(`/api/admin/creators/${encodeURIComponent(handle)}`, 'PATCH', patch)
}

export function postOAuthInvite(
  creatorHandle: string,
  expiresInDays?: number,
): Promise<{ inviteUrl: string; expiresAt: string }> {
  return mutate('/api/admin/oauth-invite', 'POST', { creatorHandle, expiresInDays })
}

// ===== Multi-platform channels (YouTube / Instagram / TikTok) =====
export function putCreatorChannel(
  handle: string,
  platform: ChannelPlatform,
  patch: { url?: string; subscriberCount?: number },
): Promise<{ channel: AdminChannel }> {
  return mutate(
    `/api/admin/creators/${encodeURIComponent(handle)}/channels/${platform}`,
    'PUT',
    patch,
  )
}

export function deleteCreatorChannel(
  handle: string,
  platform: ChannelPlatform,
): Promise<{ ok: true }> {
  return mutate(
    `/api/admin/creators/${encodeURIComponent(handle)}/channels/${platform}`,
    'DELETE',
  )
}
